import * as React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { IDatepickerSettings, setTableDatepickerAtom } from '../../../store/recoil/TableState';
import { ICheckedRecord, ICheckedTableItems, ITripsOverviewTableData } from '../interfaces';
import { DAILY_KEY } from '../constants';
import {
  checkedRecordsAtom,
  checkedTableItemsAtom,
  rideBookDataAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
} from '../TripsOverviewState';

interface IUseCheckbox {
  checkedRecords: ICheckedRecord[];
  checkOneCheckbox: (
    id: string,
    customerId: string,
    isRowChecked: boolean,
    timestamp: string
  ) => void;
  selectSectionChecbox: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectAllSectionCheckBox: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const useCheckbox = (): IUseCheckbox => {
  const tableData = useRecoilValue<ITripsOverviewTableData | null>(rideBookDataAtom);
  const checkedRecords = useRecoilValue<ICheckedRecord[]>(checkedRecordsAtom);
  const rideItemIds = useRecoilValue<string[]>(rideItemIdsAtom);
  const rideItemTimestamps = useRecoilValue<string[]>(rideItemTimestampsAtom);
  const [checkedTableItems, setCheckedTableItems] =
    useRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const datepickerSettings = useRecoilValue<IDatepickerSettings | null>(setTableDatepickerAtom);

  const getNewBlacklist = (minTimestamp: string, maxTimestamp: string) => {
    const tempData = { ...checkedTableItems };
    const rideIdBlacklist = tempData.rideIdBlacklist;
    let newRideBlacklist = {};
    Object.keys(rideIdBlacklist).forEach((customerId: string) => {
      if (!newRideBlacklist[customerId]) {
        newRideBlacklist = { ...newRideBlacklist, [customerId]: [] };
      }
      rideIdBlacklist[customerId].forEach((rideId: string) => {
        const rideIndex = rideItemIds.indexOf(rideId);
        const rideTimestamp = rideItemTimestamps[rideIndex];
        if (
          new Date(rideTimestamp) < new Date(minTimestamp) ||
          new Date(rideTimestamp) > new Date(maxTimestamp)
        ) {
          newRideBlacklist[customerId].push(rideId);
        }
      });
    });
    return newRideBlacklist;
  };

  const getNewRidelist = (minTimestamp: string, maxTimestamp: string) => {
    const tempData = { ...checkedTableItems };
    const rideIdList = tempData.rideIds;
    let newRideBlacklist = {};
    Object.keys(rideIdList).forEach((customerId: string) => {
      if (!newRideBlacklist[customerId]) {
        newRideBlacklist = { ...newRideBlacklist, [customerId]: [] };
      }
      rideIdList[customerId].forEach((rideId: string) => {
        const rideIndex = rideItemIds.indexOf(rideId);
        const rideTimestamp = rideItemTimestamps[rideIndex];
        if (
          new Date(rideTimestamp) < new Date(minTimestamp) ||
          new Date(rideTimestamp) > new Date(maxTimestamp)
        ) {
          newRideBlacklist[customerId].push(rideId);
        }
      });
    });
    return newRideBlacklist;
  };

  const getDataWithActiveIntervals = (
    min: string,
    max: string,
    customerId: string | null,
    count: string
  ) => {
    const tempCheckboxData = { ...checkedTableItems };
    const intervals = checkedTableItems.intervals.filter((i) => i.min !== min && i.max !== max);
    tempCheckboxData.intervals = intervals;
    const currentBlacklist = getNewBlacklist(min, max);
    tempCheckboxData.rideIdBlacklist = currentBlacklist;
    const currentRidelist = getNewRidelist(min, max);
    tempCheckboxData.rideIds = currentRidelist;
    tempCheckboxData.customerId = customerId;
    tempCheckboxData.counts = { ...tempCheckboxData.counts, [String(min)]: count };
    return tempCheckboxData;
  };

  const getDataWithSectionBlacklist = (
    minTimestamp: string,
    maxTimestamp: string,
    customerId: string | null,
    count: string
  ) => {
    const tempCheckboxData = { ...checkedTableItems };
    tempCheckboxData.intervalsBlacklist = [
      ...tempCheckboxData.intervalsBlacklist,
      { min: minTimestamp, max: maxTimestamp },
    ];
    tempCheckboxData.intervals = checkedTableItems.intervals.filter(
      (i) => i.min !== minTimestamp && i.max !== maxTimestamp
    );
    const currentBlacklist = getNewBlacklist(minTimestamp, maxTimestamp);
    tempCheckboxData.rideIdBlacklist = currentBlacklist;
    const currentRidelist = getNewRidelist(minTimestamp, maxTimestamp);
    tempCheckboxData.rideIds = currentRidelist;
    tempCheckboxData.customerId = customerId;
    tempCheckboxData.counts = { ...tempCheckboxData.counts, [String(minTimestamp)]: count };
    return tempCheckboxData;
  };

  const getIsSomeRideChecked = () => {
    let isSomeRideChecked = false;
    Object.values(checkedTableItems.rideIds).forEach((rides) => {
      if (rides.length > 0) {
        isSomeRideChecked = true;
      }
    });
    return isSomeRideChecked;
  };

  const getIsChecked = () => {
    return (
      checkedTableItems.mainCheckbox ||
      checkedTableItems.intervals.length > 0 ||
      getIsSomeRideChecked()
    );
  };

  const getAreCheckedRidesInInterval = (minTimestamp: string, maxTimestamp: string) => {
    const tempData = { ...checkedTableItems };
    const rideIdList = tempData.rideIds;
    let rideInInterval = false;
    Object.keys(rideIdList).forEach((customerId: string) => {
      rideIdList[customerId].forEach((rideId: string) => {
        const rideIndex = rideItemIds.indexOf(rideId);
        const rideTimestamp = rideItemTimestamps[rideIndex];
        if (
          new Date(rideTimestamp) >= new Date(minTimestamp) &&
          new Date(rideTimestamp) <= new Date(maxTimestamp)
        ) {
          rideInInterval = true;
        }
      });
    });
    return rideInInterval;
  };

  const getIsSectionChecked = (minTimestamp: string, maxTimestamp: string) => {
    const chosenInterval = checkedTableItems.intervals.find(
      (i) => i.min === minTimestamp && i.max === maxTimestamp
    );
    const chosenIntervalInBlacklist = checkedTableItems.intervalsBlacklist.find(
      (i) => i.min === minTimestamp && i.max === maxTimestamp
    );
    return (
      chosenInterval ||
      getAreCheckedRidesInInterval(minTimestamp, maxTimestamp) ||
      (checkedTableItems.mainCheckbox && !chosenIntervalInBlacklist)
    );
  };

  const selectAllSectionCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.currentTarget;

    const getMainDates = (dateSettings): string[] => {
      if (dateSettings && dateSettings.timestamp && dateSettings.timestamp.date) {
        return [dateSettings.timestamp.date[0], dateSettings.timestamp.date[1]];
      }
      return [];
    };

    const tempCheckboxData = { ...checkedTableItems };
    if (!getIsChecked()) {
      const customerId = event.currentTarget.getAttribute('data-customer-id');
      const mainDates: string[] = getMainDates(datepickerSettings);
      if (mainDates && mainDates.length === 2) {
        tempCheckboxData.mainCheckbox = true;
        tempCheckboxData.rideIdBlacklist = {};
        tempCheckboxData.intervals = [{ min: mainDates[0], max: mainDates[1] }];
        tempCheckboxData.intervalsBlacklist = [];
        tempCheckboxData.rideIds = {};
        tempCheckboxData.customerId = customerId;
      }
    } else {
      tempCheckboxData.mainCheckbox = false;
      tempCheckboxData.rideIdBlacklist = {};
      tempCheckboxData.intervals = [];
      tempCheckboxData.intervalsBlacklist = [];
      tempCheckboxData.rideIds = {};
    }
    setCheckedTableItems(tempCheckboxData);
  };

  const getIntervalData = (
    minTimestamp: string,
    maxTimestamp: string,
    customerId: string | null,
    count: string
  ) => {
    const tempCheckboxData = { ...checkedTableItems };
    if (
      !tempCheckboxData.intervals.find((i) => i.min === minTimestamp && i.max === maxTimestamp) &&
      !checkedTableItems.mainCheckbox
    ) {
      tempCheckboxData.intervals = [
        ...tempCheckboxData.intervals,
        { min: minTimestamp, max: maxTimestamp },
      ];
    }
    tempCheckboxData.intervalsBlacklist = tempCheckboxData.intervalsBlacklist.filter(
      (i) => i.min !== minTimestamp && i.max !== maxTimestamp
    );
    tempCheckboxData.rideIdBlacklist = getNewBlacklist(minTimestamp, maxTimestamp);
    tempCheckboxData.rideIds = getNewRidelist(minTimestamp, maxTimestamp);
    tempCheckboxData.customerId = customerId;
    tempCheckboxData.counts = { ...tempCheckboxData.counts, [String(minTimestamp)]: count };
    return tempCheckboxData;
  };

  const selectSectionChecbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const minTimestamp = event.currentTarget.getAttribute('data-min-timestamp');
    const maxTimestamp = event.currentTarget.getAttribute('data-max-timestamp');
    const count = event.currentTarget.getAttribute('data-count');
    const customerId = event.currentTarget.getAttribute('data-customer-id');
    if (checkedRecords && minTimestamp && maxTimestamp) {
      if (getIsSectionChecked(minTimestamp, maxTimestamp)) {
        setCheckedTableItems(
          checkedTableItems.mainCheckbox
            ? getDataWithSectionBlacklist(minTimestamp, maxTimestamp, customerId, count || '')
            : getDataWithActiveIntervals(minTimestamp, maxTimestamp, customerId, count || '')
        );
      } else {
        setCheckedTableItems(getIntervalData(minTimestamp, maxTimestamp, customerId, count || ''));
      }
    }
  };

  const rideIsNotInInterval = (rideId: string) => {
    const rideIndex = rideItemIds.indexOf(rideId);
    const rideTimestamp = rideItemTimestamps[rideIndex];
    return (
      !checkedTableItems.intervals.find((i) => i.min <= rideTimestamp && i.max >= rideTimestamp) ||
      checkedTableItems.intervalsBlacklist.find(
        (i) => i.min <= rideTimestamp && i.max >= rideTimestamp
      )
    );
  };

  const getSectionTimestampAndCount = (timestamp: string) => {
    const summary = tableData?.summaries.find(
      (i) =>
        i.type === DAILY_KEY &&
        new Date(timestamp) >= new Date(i.from) &&
        new Date(timestamp) <= new Date(i.to)
    );
    if (summary) {
      return { [String(summary.from)]: String(summary.rideCount) };
    }
    return {};
  };

  const checkOneCheckbox = (
    id: string,
    customerId: string,
    isRowChecked: boolean,
    timestamp: string
  ) => {
    const tempData = { ...checkedTableItems };
    const checkBlacklist = !!(tempData.intervals.length > 0 || tempData.mainCheckbox);
    const rideIds: string[] = tempData.rideIds[customerId] || [];
    const blackRideIds: string[] = tempData.rideIdBlacklist[customerId] || [];
    if (isRowChecked) {
      const newRideIds = rideIds.filter((i) => i !== id);
      tempData.rideIds = { ...tempData.rideIds, [customerId]: newRideIds };
      if (checkBlacklist) {
        const newBlackRideIds = [...blackRideIds, id];
        tempData.rideIdBlacklist = { ...tempData.rideIdBlacklist, [customerId]: newBlackRideIds };
      }
    } else {
      if (rideIsNotInInterval(id)) {
        const newRideIds = [...rideIds, id];
        tempData.rideIds = { ...tempData.rideIds, [customerId]: newRideIds };
      }
      if (checkBlacklist) {
        const newBlackRideIds = blackRideIds.filter((i) => i !== id);
        tempData.rideIdBlacklist = { ...tempData.rideIdBlacklist, [customerId]: newBlackRideIds };
      }
    }
    tempData.customerId = customerId;
    const sectionTimestampAndCount = getSectionTimestampAndCount(timestamp);
    if (
      !tempData.counts ||
      (tempData.counts && !tempData.counts[Object.keys(sectionTimestampAndCount)[0]])
    ) {
      tempData.counts = { ...checkedTableItems.counts, ...sectionTimestampAndCount };
    }
    setCheckedTableItems(tempData);
  };

  return { checkedRecords, checkOneCheckbox, selectSectionChecbox, selectAllSectionCheckBox };
};

export default useCheckbox;
